import type { Session, User } from '@supabase/supabase-js';
import type {
  CitationBundle,
  CreateMatterInput,
  FactSheetV1,
  Matter,
  MatterDocument,
} from '../chat/types';
import type {
  ClaimChart,
  ClaimChartBundle,
  ClaimChartCell,
  ClaimChartColumn,
  ClaimChartRow,
  CreateChartInput,
  CreateColumnInput as CreateChartColumnInput,
  CreateRowInput as CreateChartRowInput,
} from '../claim-charts/types';
import type {
  CreateColumnInput as CreateReviewColumnInput,
  CreateReviewInput,
  CreateRowInput as CreateReviewRowInput,
  TabularCell,
  TabularColumn,
  TabularReview,
  TabularReviewBundle,
  TabularRow,
} from '../tabular/types';
import type { MatterDocumentWithFolder, MatterFolder } from '../matters/types';
import type {
  CreateWorkflowInput,
  UpdateWorkflowInput,
  WorkflowRow,
} from '../workflows/types';
import {
  captureDemoScenarioFromUrl,
  documentUploadFixture,
  getDemoClaimChartDetailState,
  getDemoClaimChartState,
  getDemoDocumentUploadState,
  getDemoMatterState,
  getDemoScenario,
  getDemoTabularReviewDetailState,
  getDemoTabularReviewState,
  getDemoWorkflowState,
  longContentFixture,
  reasoningTimelineFixture,
  researchTableTimelineFixture,
  workflowFixture,
} from './scenarios';
import { BUILTIN_WORKFLOWS } from '../workflows/builtins';

const DEMO_KEY = 'prometheus_dev_demo_session';
const DEMO_USER_ID = '00000000-0000-4000-8000-000000000001';
const NOW = '2026-07-15T14:30:00.000Z';
const DEMO_NEW_CONVERSATION_TIMESTAMP = '2026-07-23T17:00:00.000Z';

/**
 * Development-only product harness.
 *
 * Visiting any local route with `?demo=1` enables the harness for the tab so
 * navigation can drop the query string without dropping the session. The
 * import.meta.env.DEV guard is deliberately repeated here and at the auth /
 * access boundaries: a production bundle can never use this as an auth path.
 */
export function isDemoMode(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;
  try {
    const requested = new URLSearchParams(window.location.search).get('demo') === '1';
    if (requested) {
      window.sessionStorage.setItem(DEMO_KEY, '1');
      captureDemoScenarioFromUrl();
    }
    return requested || window.sessionStorage.getItem(DEMO_KEY) === '1';
  } catch {
    return new URLSearchParams(window.location.search).get('demo') === '1';
  }
}

export const demoUser = {
  id: DEMO_USER_ID,
  aud: 'authenticated',
  role: 'authenticated',
  email: 'alex.morgan@example.com',
  email_confirmed_at: NOW,
  phone: '',
  app_metadata: { provider: 'demo', providers: ['demo'] },
  user_metadata: { display_name: 'Alex Morgan' },
  identities: [],
  created_at: '2026-01-08T10:00:00.000Z',
  updated_at: NOW,
} as unknown as User;

export const demoSession = {
  access_token: 'development-demo-token',
  token_type: 'bearer',
  expires_in: 3600,
  expires_at: 4_102_444_800,
  refresh_token: 'development-demo-refresh',
  user: demoUser,
} as Session;

export const demoFactSheet: FactSheetV1 = {
  schema_version: 1,
  regime: 'AIA',
  applicant_type: 'juristic_entity',
  matter_type: 'prosecution',
  application_number: '18/456,789',
  patent_number: null,
  procedural_posture: 'Non-final Office Action response due September 18, 2026',
  asserted_facts: [
    'Independent claim 1 was rejected under 35 U.S.C. § 103.',
    'The examiner combines Chen with Alvarez.',
  ],
  open_questions: [
    'Does Chen disclose the claimed adaptive threshold?',
    'Is there a supported reason to combine the references?',
  ],
  last_updated_turn: 3,
};

export const demoMatters: Matter[] = [
  {
    id: 'matter-aurora',
    user_id: DEMO_USER_ID,
    name: 'Aurora Sensor Platform',
    matter_number: 'ACME-1042-US',
    domain: 'patent',
    client_name: 'Acme Robotics',
    adverse_party: null,
    patent_number: null,
    application_number: '18/456,789',
    case_number: null,
    court: null,
    status: 'active',
    created_at: '2026-02-02T16:00:00.000Z',
    updated_at: NOW,
  },
  {
    id: 'matter-northstar',
    user_id: DEMO_USER_ID,
    name: 'Northstar FTO Review',
    matter_number: 'NOVA-778-FTO',
    domain: 'patent',
    client_name: 'Nova Mobility',
    adverse_party: null,
    patent_number: null,
    application_number: null,
    case_number: null,
    court: null,
    status: 'active',
    created_at: '2026-03-11T15:00:00.000Z',
    updated_at: '2026-07-12T18:00:00.000Z',
  },
];

type DemoConversation = {
  id: string;
  title: string | null;
  updated_at: string;
  matter_id: string | null;
};

const demoConversations: DemoConversation[] = [
  {
    id: 'demo-conversation-oa',
    title: 'Responding to the Chen–Alvarez rejection',
    updated_at: NOW,
    matter_id: 'matter-aurora',
  },
  {
    id: 'demo-conversation-construction',
    title: 'Construction of “adaptive threshold”',
    updated_at: '2026-07-14T17:10:00.000Z',
    matter_id: 'matter-aurora',
  },
  {
    id: 'demo-conversation-fto',
    title: 'Northstar preliminary FTO issues',
    updated_at: '2026-07-12T18:00:00.000Z',
    matter_id: 'matter-northstar',
  },
  {
    id: 'demo-conversation-research-table',
    title: 'Adaptive-threshold authority table',
    updated_at: '2026-07-16T16:20:00.000Z',
    matter_id: 'matter-aurora',
  },
];

const emptyCitations: CitationBundle = {
  sections: [],
  statutes: [],
  regulations: [],
  guidance: [],
  ptab: [],
  cases: [],
};

const demoCitations: CitationBundle = {
  ...emptyCitations,
  sections: [
    {
      id: 'mpep-2143',
      title: 'MPEP § 2143 — Examples of Basic Requirements of a Prima Facie Case',
      url: 'https://www.uspto.gov/web/offices/pac/mpep/s2143.html',
      source_type: 'mpep',
      anchor_text: 'a reason why a person of ordinary skill would have combined the prior art',
    },
  ],
  statutes: [
    {
      id: '35-usc-103',
      title: '35 U.S.C. § 103',
      url: 'https://www.law.cornell.edu/uscode/text/35/103',
      source_type: 'statute',
    },
  ],
  cases: [
    {
      case_name: 'KSR International Co. v. Teleflex Inc.',
      citation: '550 U.S. 398 (2007)',
      doctrines: ['obviousness', 'motivation to combine'],
      source: 'retrieved',
      holding_text: 'The analysis must identify an apparent reason to combine the known elements.',
      spine_id: 101,
    },
  ],
};

export const sourceManifestCitations: CitationBundle = {
  ...emptyCitations,
  cases: [
    {
      case_name: 'In re Wands',
      citation: '858 F.2d 731 (Fed. Cir. 1988)',
      doctrines: ['enablement'],
      source: 'retrieved',
      cafc_opinion_id: 'demo-opinion-wands',
      holding_text: 'Enablement depends on whether experimentation is undue.',
    },
    {
      case_name: 'Citation Spine Match v. Example Industries',
      citation: '999 F.4th 101 (Fed. Cir. 2025)',
      doctrines: ['claim construction'],
      source: 'training_verified',
      spine_id: 4101,
    },
    {
      case_name: 'Model Memory Holdings v. Unverified Systems',
      citation: '888 F.3d 202 (Fed. Cir. 2024)',
      doctrines: ['obviousness'],
      source: 'training',
    },
  ],
  sections: [
    {
      id: 'mpep-long-source-manifest',
      title: `MPEP § 2164.01(a) — ${longContentFixture.identifier}`,
      url: 'https://www.uspto.gov/web/offices/pac/mpep/s2164.html',
      source_type: 'mpep',
      anchor_text: 'The disclosure must teach a person of ordinary skill how to make and use the invention.',
    },
  ],
  web_sources: [
    {
      title: 'USPTO examination guidance for enablement',
      url: 'https://www.uspto.gov/patents/laws/examination-policy/enablement',
      source_type: 'web_search',
      retrieved_date: '2026-07-23',
    },
    {
      title: 'Reported external authority with no source target',
      source_type: 'web_search',
      retrieved_date: '2026-07-23',
    },
  ],
};

type DemoRawTurn = {
  id: string;
  conversation_id: string;
  turn_index: number;
  role: string;
  content: string;
  attached_files: unknown;
  intent: string | null;
  artifact_type: string | null;
  artifact: unknown;
  citations: unknown;
  corpus_gaps: unknown;
  diagnostic?: unknown;
  events: unknown;
  retrieved_chunk_ids?: string[] | null;
  created_at: string;
};

const demoTurns: Record<string, DemoRawTurn[]> = {
  'demo-conversation-oa': [
    {
      id: 'demo-user-1',
      conversation_id: 'demo-conversation-oa',
      turn_index: 0,
      role: 'user',
      content: 'What is our strongest response to the Chen–Alvarez obviousness rejection?',
      attached_files: null,
      intent: 'multi_step_reasoning',
      artifact_type: null,
      artifact: null,
      citations: null,
      corpus_gaps: null,
      events: null,
      created_at: '2026-07-15T14:20:00.000Z',
    },
    {
      id: 'demo-assistant-1',
      conversation_id: 'demo-conversation-oa',
      turn_index: 1,
      role: 'assistant',
      content:
        '## Recommended response\n\nLead with the missing **adaptive threshold** limitation. Chen changes a threshold only after a fixed calibration cycle; claim 1 requires continuous adjustment based on the live confidence value. Alvarez teaches confidence scoring, but not using that score to change the threshold.\n\n### Why this is the strongest path\n\n1. **No complete teaching.** Neither reference discloses the claimed feedback relationship under § 103. [3]\n2. **Weak combination rationale.** The Office Action says the references are “compatible,” but does not explain why a skilled artisan would replace Chen’s fixed calibration with Alvarez’s scoring output. The MPEP requires an articulated reason to combine. [2]\n3. **Drafting strategy.** Quote the exact Chen passage, distinguish the claimed control loop, and preserve a secondary argument under **KSR**. [1]\n\nI would avoid relying primarily on “teaching away”; the current record supports a missing-limitation argument more cleanly.',
      attached_files: null,
      intent: 'multi_step_reasoning',
      artifact_type: 'prose_answer',
      artifact: null,
      citations: demoCitations,
      corpus_gaps: { gap_detected: false, unverified_citations: [], message: '' },
      diagnostic: {
        request_id: '11111111-1111-4111-8111-111111111111',
        retrieval: {
          primary_chunks: 8,
          xref_chunks: 4,
          inherited_chunks: 0,
          case_law_included: true,
          source_types_used: ['mpep', 'statute', 'caselaw'],
          retrieval_mode: 'nano',
        },
        tools_executed: [{ name: 'search_corpus', status: 'succeeded', error_message: null }],
        provenance_gate: {
          mode: 'soft',
          passed: true,
          unverified_count: 0,
          empty_retrieval_ungrounded: false,
          total_citations: 3,
          banner_applied: false,
        },
        answer_extraction: { status: 'bare', sample: null },
      },
      events: [
        { type: 'step', id: 'facts', label: 'Reviewing matter facts', status: 'complete' },
        {
          type: 'tool_call',
          tool: 'search_corpus',
          input_summary: 'Obviousness standards and reason-to-combine authority',
        },
        {
          type: 'tool_result',
          tool: 'search_corpus',
          result_summary: 'Found 8 primary-law passages and MPEP guidance',
        },
        reasoningTimelineFixture.canonicalEvents.find(
          (event) => event.type === 'reasoning',
        ),
        { type: 'step', id: 'synthesis', label: 'Testing the rejection against claim 1', status: 'complete' },
        { type: 'complete' },
      ].filter(Boolean),
      retrieved_chunk_ids: ['mpep-2143', '35-usc-103', 'case-spine-101'],
      created_at: '2026-07-15T14:21:00.000Z',
    },
  ],
  'demo-conversation-construction': [],
  'demo-conversation-fto': [],
  'demo-conversation-research-table': [
    {
      id: 'demo-user-research-table',
      conversation_id: 'demo-conversation-research-table',
      turn_index: 0,
      role: 'user',
      content: 'Build a research table for adaptive-threshold obviousness authority.',
      attached_files: null,
      intent: 'structured_mode',
      artifact_type: null,
      artifact: null,
      citations: null,
      corpus_gaps: null,
      events: null,
      created_at: '2026-07-16T16:19:00.000Z',
    },
    {
      id: 'demo-assistant-research-table',
      conversation_id: 'demo-conversation-research-table',
      turn_index: 1,
      role: 'assistant',
      content: 'Research table: 3 cases, 2 chunks',
      attached_files: null,
      intent: 'structured_mode',
      artifact_type: 'research_table',
      artifact: researchTableTimelineFixture.artifact,
      citations: null,
      corpus_gaps: null,
      events: researchTableTimelineFixture.persistedEvents,
      created_at: '2026-07-16T16:20:00.000Z',
    },
  ],
};

export function listDemoConversations(): DemoConversation[] {
  return structuredClone(demoConversations);
}

export function renameDemoConversation(id: string, title: string): void {
  const row = demoConversations.find((item) => item.id === id);
  if (row) row.title = title;
}

export function deleteDemoConversations(ids: string[]): void {
  for (const id of ids) {
    const index = demoConversations.findIndex((item) => item.id === id);
    if (index >= 0) demoConversations.splice(index, 1);
  }
}

export function loadDemoConversation(id: string) {
  const row = demoConversations.find((item) => item.id === id) ?? demoConversations[0]!;
  return {
    conversation: {
      ...structuredClone(row),
      fact_sheet: structuredClone(demoFactSheet),
      status: 'active',
      created_at: '2026-07-15T14:20:00.000Z',
    },
    turns: structuredClone(demoTurns[id] ?? demoTurns['demo-conversation-oa'] ?? []),
  };
}

let demoMatterCreateAttempts = 0;

export async function loadDemoMatters(): Promise<Matter[]> {
  const state = getDemoMatterState();
  if (state === 'slow') {
    // Keep the pending route observable even when the app and browser start
    // unusually quickly in the single-worker CI configuration.
    await new Promise((resolve) => setTimeout(resolve, 1_200));
  }
  if (state === 'load-failure') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    throw new Error('Matters could not be loaded. Check your connection and try again.');
  }
  if (state === 'unauthorized') {
    throw Object.assign(
      new Error('private matter authorization policy detail'),
      { status: 403, code: '42501' },
    );
  }
  const rows = state === 'empty'
    ? demoMatters.filter((matter) => matter.id.startsWith('demo-matter-'))
    : state === 'long'
      ? demoMatters.map((matter, index) =>
          index === 0
            ? {
                ...matter,
                name: `Aurora Sensor Platform — ${longContentFixture.identifier}`,
                matter_number: longContentFixture.filename,
                client_name: longContentFixture.url,
              }
            : matter,
        )
      : demoMatters;
  return structuredClone(rows);
}

export function createDemoMatter(input: CreateMatterInput): Matter {
  if (getDemoMatterState() === 'create-failure' && demoMatterCreateAttempts++ === 0) {
    throw new Error('Matter creation was not saved. Review the details and try again.');
  }
  const matter: Matter = {
    ...demoMatters[0]!,
    id: `demo-matter-${crypto.randomUUID()}`,
    name: input.name.trim(),
    matter_number: input.matter_number?.trim() || null,
    domain: input.domain ?? 'patent',
    client_name: input.client_name?.trim() || null,
    adverse_party: input.adverse_party?.trim() || null,
    patent_number: input.patent_number?.trim() || null,
    application_number: input.application_number?.trim() || null,
    case_number: input.case_number?.trim() || null,
    court: input.court?.trim() || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  demoMatters.unshift(matter);
  return structuredClone(matter);
}

export function createDemoChatResponse(opts: {
  conversation_id: string | null;
  user_message: string;
  matter_id?: string | null;
  mode?: 'narrative' | 'research_table';
}): Response {
  const scenario = getDemoScenario();
  const conversationId = opts.conversation_id ?? `demo-conversation-${crypto.randomUUID()}`;
  const userTurnId = `demo-user-${crypto.randomUUID()}`;
  const assistantTurnId = `demo-assistant-${crypto.randomUUID()}`;
  const requestId = crypto.randomUUID();
  if (scenario === 'rate-limited') {
    return new Response(
      JSON.stringify({
        error: 'Request capacity is temporarily exhausted. Retry in a moment.',
      }),
      {
        status: 429,
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
          'Access-Control-Expose-Headers': 'X-Request-Id',
        },
      },
    );
  }
  if (!opts.conversation_id) {
    demoConversations.unshift({
      id: conversationId,
      title: opts.user_message.slice(0, 72),
      updated_at: DEMO_NEW_CONVERSATION_TIMESTAMP,
      matter_id: opts.matter_id ?? null,
    });
  }

  const encoder = new TextEncoder();
  const events: Array<{ type: string; data: Record<string, unknown>; delay?: number }> = [];

  if (opts.mode === 'research_table') {
    const baseArtifact = structuredClone(researchTableTimelineFixture.artifact);
    const artifact = scenario === 'zero-results'
      ? {
          ...baseArtifact,
          rows: [],
          total_cases: 0,
          total_chunks: 0,
          source_types: [],
        }
      : scenario === 'partial-stream-failure'
        ? {
            ...baseArtifact,
            rows: baseArtifact.rows.slice(0, 2),
            total_cases: 2,
            total_chunks: 0,
            source_types: ['case'],
          }
        : baseArtifact;
    artifact.query = opts.user_message;
    const rows = artifact.rows;
    events.push(
      { type: 'status', data: { step: 'retrieval', message: 'Searching primary sources' } },
      {
        type: 'research_results',
        data: {
          rows,
          total_cases: artifact.total_cases,
          total_chunks: artifact.total_chunks,
          source_types: artifact.source_types,
          query: opts.user_message,
        },
        delay: 180,
      },
    );
    if (scenario === 'partial-stream-failure') {
      events.push({
        type: 'error',
        data: {
          error_code: 'research_partial_failure',
          message: 'Research retrieval stopped after partial results. Review the available rows or retry.',
        },
        delay: 420,
      });
    } else {
      events.push({
        type: 'result',
        data: {
          turn_id: assistantTurnId,
          user_turn_id: userTurnId,
          conversation_id: conversationId,
          artifact: {
            artifact_type: 'research_table',
            artifact_id: assistantTurnId,
            payload: artifact,
          },
          citations: demoCitations,
          corpus_gaps: null,
          fact_sheet: demoFactSheet,
        },
        delay: scenario === 'slow-first-response' ? 1800 : 420,
      });
    }
  } else {
    const zeroResults = scenario === 'zero-results';
    const keywordDegraded = scenario === 'keyword-degraded';
    const invalidCitation = scenario === 'invalid-citation';
    const sourceManifest = scenario === 'source-manifest';
    const partialFailure = scenario === 'partial-stream-failure';
    const answer = chooseScenarioAnswer(scenario, opts.user_message);
    const chunks = Array.from(
      { length: Math.ceil(answer.length / 92) },
      (_, index) => answer.slice(index * 92, (index + 1) * 92),
    );
    const citations = zeroResults
      ? emptyCitations
      : sourceManifest
        ? sourceManifestCitations
        : demoCitations;
    const retrievedChunkIds = zeroResults
      ? []
      : sourceManifest
        ? ['demo-opinion-wands', 'spine:4101', 'mpep-long-source-manifest']
        : ['mpep-2143', '35-usc-103', 'case-spine-101'];
    const diagnostic = {
      request_id: requestId,
      retrieval: {
        primary_chunks: zeroResults ? 0 : 8,
        xref_chunks: zeroResults ? 0 : 4,
        inherited_chunks: 0,
        case_law_included: !zeroResults,
        source_types_used: zeroResults ? [] : ['mpep', 'statute', 'caselaw'],
        retrieval_mode: keywordDegraded ? 'keyword_degraded' : 'nano',
      },
      tools_executed: [{
        name: 'search_corpus',
        status: 'succeeded',
        error_message: null,
        result_count: zeroResults ? 0 : 12,
      }],
      provenance_gate: {
        mode: 'soft',
        passed: !zeroResults && !invalidCitation && !sourceManifest,
        unverified_count: sourceManifest ? 3 : invalidCitation ? 1 : 0,
        empty_retrieval_ungrounded: zeroResults,
        total_citations: zeroResults ? 0 : sourceManifest ? 7 : invalidCitation ? 4 : 3,
        banner_applied: zeroResults || invalidCitation || sourceManifest,
      },
      answer_extraction: { status: 'bare', sample: null },
    };
    events.push(
      {
        type: 'step',
        data: { id: 'scope', status: 'start', label: 'Scoping the legal issue' },
        delay: scenario === 'slow-first-response' ? 1800 : 70,
      },
      {
        type: 'step',
        data: { id: 'scope', status: 'complete', label: 'Scoping the legal issue' },
        // The deliberately slow UX fixture must leave its named progress
        // state observable under a single-worker, shared-CPU Ubuntu run.
        delay: scenario === 'slow-first-response' ? 900 : 120,
      },
      {
        type: 'tool_call',
        data: {
          tool: 'search_corpus',
          input_summary: 'Controlling authority and practitioner guidance',
          tool_use_id: 'demo-tool-1',
        },
        delay: 140,
      },
      {
        type: 'tool_result',
        data: {
          tool: 'search_corpus',
          result_summary: zeroResults
            ? 'No matching primary-source passages'
            : keywordDegraded
              ? 'Found 12 passages using keyword fallback'
              : 'Found 12 relevant primary-source passages',
          tool_use_id: 'demo-tool-1',
        },
        delay: 180,
      },
      ...(keywordDegraded
        ? [{
            type: 'step',
            data: {
              id: 'retrieving',
              status: 'complete',
              label: 'Searching legal authorities',
              detail: '12 passages · keyword-only (semantic search degraded)',
            },
            delay: 40,
          }]
        : []),
      ...(scenario === 'happy-path'
        ? [
            {
              type: 'thinking',
              data: { text: 'Compare the exact claim language first. ', isStreaming: true },
              delay: 30,
            },
            {
              type: 'thinking',
              data: {
                text: 'Then test whether the record supplies an articulated reason to combine.',
                isStreaming: true,
              },
              delay: 30,
            },
          ]
        : []),
      {
        type: 'fact_sheet_update',
        data: { fact_sheet: demoFactSheet, contradiction_detected: false, contradiction_note: null },
      },
      {
        type: 'citations_ready',
        data: { citations },
        delay: 40,
      },
    );
    const emittedChunks = partialFailure ? chunks.slice(0, Math.max(2, Math.ceil(chunks.length / 3))) : chunks;
    for (const [index, text] of emittedChunks.entries()) {
      events.push({
        type: 'text_delta',
        data: { text },
        delay: sourceManifest && index === 0 ? 2500 : 42,
      });
    }
    if (partialFailure) {
      events.push({
        type: 'error',
        data: {
          error_code: 'stream_disconnected',
          message: 'The response connection ended unexpectedly. Your partial answer was preserved.',
        },
        delay: 240,
      });
    } else {
      events.push({
        type: 'turn_complete',
        data: {
          turn_id: assistantTurnId,
          user_turn_id: userTurnId,
          conversation_id: conversationId,
          citations,
          corpus_gaps: zeroResults
            ? {
                gap_detected: true,
                unverified_citations: [],
                message: 'No matching corpus material was found for this query. Refine the terms or add a document.',
              }
            : invalidCitation
              ? {
                  gap_detected: true,
                  unverified_citations: ['[99]'],
                  message: 'One citation identifier could not be matched to retrieved evidence.',
                }
              : sourceManifest
                ? {
                    gap_detected: true,
                    unverified_citations: ['[3]', 'external:missing-target', '[99]'],
                    message: 'One model-supplied citation needs verification, one reported source has no inspectable target, and one citation marker is invalid.',
                  }
              : { gap_detected: false, unverified_citations: [], message: '' },
          fact_sheet: demoFactSheet,
          retrieved_chunk_ids: retrievedChunkIds,
          diagnostic,
        },
        delay: 80,
      });
    }
  }

  let cancelled = false;
  const stream = new ReadableStream<Uint8Array>({
    async start(controller) {
      for (const event of events) {
        await new Promise((resolve) => window.setTimeout(resolve, event.delay ?? 70));
        if (cancelled) return;
        controller.enqueue(
          encoder.encode(`event: ${event.type}\ndata: ${JSON.stringify(event.data)}\n\n`),
        );
      }
      if (!cancelled) controller.close();
    },
    cancel() {
      cancelled = true;
    },
  });
  return new Response(stream, {
    status: 200,
    headers: {
      'Content-Type': 'text/event-stream',
      'X-Request-Id': requestId,
      'Access-Control-Expose-Headers': 'X-Request-Id',
    },
  });
}

function chooseScenarioAnswer(
  scenario: ReturnType<typeof getDemoScenario>,
  message: string,
): string {
  if (scenario === 'zero-results') {
    return '## No matching authority found\n\nI could not locate corpus material that directly supports an answer to this query. That is an absence-of-results finding, not a conclusion about the law.\n\n### Recovery options\n\n- Add the relevant Office Action or reference to the matter.\n- Search with a patent number, application number, case name, or exact claim phrase.\n- Broaden the date or source scope and try again.';
  }
  if (scenario === 'invalid-citation') {
    return '## Review required\n\nThe retrieved record supports the general obviousness framework [1], but this sentence intentionally refers to an identifier that was not supplied by retrieval [99].\n\nThe provenance gate caught the mismatch, so this answer must not be treated as fully verified until that assertion is removed or linked to an inspectable source.';
  }
  if (scenario === 'source-manifest') {
    return '## Source manifest audit\n\nThe record includes a corpus opinion [1], a case identity matched only to the citation spine [2], a training-only case that still needs verification [3], and a deliberately long corpus label [4]. The manifest also reports an unnumbered external web source and an unnumbered source whose target is unavailable. This invalid marker must remain plain text rather than becoming an inspectable source [99].\n\nEach entry must retain its own provenance and inspection limits.';
  }
  if (scenario === 'very-long-answer') {
    const adversarialContent = [
      '### Unbroken-content audit',
      '',
      `Canonical source: [${longContentFixture.url}](${longContentFixture.url})`,
      '',
      `Record identifier: \`${longContentFixture.identifier}\``,
      '',
      `Filename: \`${longContentFixture.filename}\``,
      '',
      `| ${longContentFixture.tableHeader} | Disposition |`,
      '| --- | --- |',
      `| ${longContentFixture.identifier} | Preserve for review |`,
      '',
      '```ts',
      `${longContentFixture.codeMarker} = "${longContentFixture.identifier}";`,
      'const filingName = prosecutionRecord + ".docx";',
      '```',
    ].join('\n');
    const sections = Array.from({ length: 9 }, (_, index) => {
      const number = index + 1;
      return `### ${number}. Analysis checkpoint with deliberately extended heading text\n\nThe record at checkpoint ${number} should be compared against the exact claim language, the cited passage, and the examiner’s stated rationale. This paragraph is intentionally long enough to exercise reading width, wrapping, citation placement, and narrow-panel behavior without relying on meaningless placeholder text. The primary authority remains inspectable through the source manifest [${(index % 3) + 1}].\n\n- Preserve the strongest record-based distinction.\n- Separate missing disclosure from motivation to combine.\n- State uncertainty where the available record is incomplete.`;
    });
    return `## Extended prosecution analysis\n\nThis deterministic answer exercises long-content behavior across desktop and constrained layouts.\n\n${adversarialContent}\n\n${sections.join('\n\n')}\n\n## Recommended disposition\n\nLead with the narrowest supported distinction, retain the combination-rationale argument as an independent fallback, and verify every quoted passage before filing.`;
  }
  return chooseDemoAnswer(message);
}

function chooseDemoAnswer(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes('claim') || normalized.includes('obvious')) {
    return '## Short answer\n\nThe strongest response is the **missing adaptive-threshold limitation** under § 103 [3], with the unsupported reason to combine as a separate fallback. Chen uses a fixed calibration cycle; Alvarez supplies a confidence score but never feeds it back into the threshold control required by claim 1.\n\n### Suggested structure\n\n- Quote the claim language and the closest Chen passage side by side.\n- Explain the missing feedback relationship in one concrete sentence.\n- Tie the missing rationale to the MPEP’s articulated-reason requirement. [2]\n- Challenge the Office Action’s compatibility rationale under **KSR** [1] without overreaching into a teaching-away argument.\n- Preserve an amendment only as a fallback, not as the lead position.\n\nThis keeps the response anchored to what the references actually disclose and gives the examiner a clean path to withdraw the rejection.';
  }
  if (normalized.includes('draft') || normalized.includes('memo')) {
    return '## Drafting plan\n\nI would organize the memo around the governing standard, the record facts that matter to that standard, and a recommendation section that separates the lead position from fallback options.\n\nThe current matter record supports a concise primary argument and a short risk note rather than a long survey of every possible doctrine.';
  }
  return '## Analysis\n\nThe current record supports a focused answer built from the controlling primary authority and the matter facts already captured in the workspace.\n\n### Practical next step\n\nConfirm the precise procedural posture and the claim language you want treated as controlling. I can then turn this into either a client-ready memo, an Office Action response outline, or a comparison table.';
}

// ---------------------------------------------------------------------------
// Claim charts
// ---------------------------------------------------------------------------

const demoChart: ClaimChart = {
  id: 'demo-chart-1',
  user_id: DEMO_USER_ID,
  matter_id: 'matter-aurora',
  name: 'Claim 1 — Chen / Alvarez',
  patent_number: 'US 12,345,678',
  description: 'Obviousness mapping for the pending independent claim.',
  chart_type: 'obviousness',
  status: 'active',
  created_at: '2026-07-10T15:00:00.000Z',
  updated_at: NOW,
  evaluations: null,
};

const demoChartColumns: ClaimChartColumn[] = [
  {
    id: 'chart-col-chen', chart_id: demoChart.id, column_type: 'reference', name: 'Chen',
    description: 'Primary reference', reference_citation: 'US 2024/0123456', reference_number: 'US20240123456A1', ordering: 0, created_at: NOW,
  },
  {
    id: 'chart-col-alvarez', chart_id: demoChart.id, column_type: 'reference', name: 'Alvarez',
    description: 'Secondary reference', reference_citation: 'US 2023/0098765', reference_number: 'US20230098765A1', ordering: 1, created_at: NOW,
  },
];

const demoChartRows: ClaimChartRow[] = [
  {
    id: 'chart-row-1', chart_id: demoChart.id, claim_number: 1, limitation_index: 0,
    limitation_text: 'A sensing system comprising a processor configured to determine a confidence value from a live sensor stream;', construction_notes: null, ordering: 0, created_at: NOW,
  },
  {
    id: 'chart-row-2', chart_id: demoChart.id, claim_number: 1, limitation_index: 1,
    limitation_text: 'continuously adjust an operating threshold based on the confidence value;', construction_notes: '“Continuously” requires repeated adjustment during operation.', ordering: 1, created_at: NOW,
  },
  {
    id: 'chart-row-3', chart_id: demoChart.id, claim_number: 1, limitation_index: 2,
    limitation_text: 'and generate an alert when the sensor stream crosses the adjusted operating threshold.', construction_notes: null, ordering: 2, created_at: NOW,
  },
];

const demoChartCells: ClaimChartCell[] = demoChartRows.flatMap((row, rowIndex) =>
  demoChartColumns.map((column, columnIndex) => ({
    chart_id: demoChart.id,
    row_id: row.id,
    column_id: column.id,
    mapping_text:
      rowIndex === 1
        ? columnIndex === 0
          ? 'Chen recalibrates after a fixed interval, but does not use a confidence value for continuous adjustment.'
          : 'Alvarez calculates confidence scores but does not change an operating threshold.'
        : `${column.name} describes the corresponding sensing and alert functionality.`,
    citations: [{ label: column.reference_citation ?? column.name, source_id: column.reference_number ?? undefined }],
    strength: rowIndex === 1 ? 'weak' : 'strong',
    notes: rowIndex === 1 ? 'Potential missing limitation.' : null,
    status: 'ready',
    error_message: null,
    last_regenerated_at: NOW,
    created_at: NOW,
    updated_at: NOW,
  })),
);

const demoCharts: ClaimChart[] = [structuredClone(demoChart)];
let demoChartListAttempts = 0;
let demoChartCreateAttempts = 0;
let demoChartSequence = 0;
let demoChartColumnAttempts = 0;
let demoChartRowAttempts = 0;
const demoChartCellAttempts = new Map<string, number>();
let demoWorkflowLaunchChartAttempts = 0;
let demoWorkflowLaunchColumnAttempts = 0;
let demoWorkflowLaunchCleanupAttempts = 0;

export class DemoClaimChartFixtureError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'DemoClaimChartFixtureError';
  }
}

/** Reset mutable claim-chart demo state for deterministic focused tests. */
export function resetDemoClaimCharts(): void {
  demoCharts.splice(0, demoCharts.length, structuredClone(demoChart));
  demoChartListAttempts = 0;
  demoChartCreateAttempts = 0;
  demoChartSequence = 0;
  demoChartColumnAttempts = 0;
  demoChartRowAttempts = 0;
  demoChartCellAttempts.clear();
  demoWorkflowLaunchChartAttempts = 0;
  demoWorkflowLaunchColumnAttempts = 0;
  demoWorkflowLaunchCleanupAttempts = 0;
}

export async function listDemoCharts(): Promise<ClaimChart[]> {
  const state = getDemoClaimChartState();
  if (state === 'slow') {
    await new Promise((resolve) => setTimeout(resolve, 1_200));
  }
  if (state === 'load-failure' && demoChartListAttempts++ === 0) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    throw new Error('Claim charts could not be loaded. Check your connection and try again.');
  }
  if (state === 'unauthorized') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    throw new DemoClaimChartFixtureError(
      403,
      'private claim-chart list authorization policy detail',
    );
  }
  const rows = state === 'empty'
    ? demoCharts.filter((chart) => chart.id.startsWith('demo-chart-created-'))
    : state === 'long'
      ? demoCharts.map((chart, index) =>
          index === 0
            ? {
                ...chart,
                name: `Claim chart — ${longContentFixture.identifier}`,
                patent_number: longContentFixture.filename,
                description: longContentFixture.url,
              }
            : chart,
        )
      : demoCharts;
  return structuredClone(rows);
}

export async function loadDemoChartBundle(
  id: string,
  options: { retry?: boolean } = {},
): Promise<ClaimChartBundle> {
  const state = getDemoClaimChartDetailState();
  if (state === 'slow') {
    await waitForDemoClaimChartDelay(1_200);
  }
  // Key this fixture to the user-visible retry intent rather than a request
  // count: React's development StrictMode can restart initial effects, so
  // attempt counters do not reliably identify the explicit recovery action.
  if (state === 'failed-once' && !options.retry) {
    await waitForDemoClaimChartDelay(300);
    throw new DemoClaimChartFixtureError(
      503,
      'private claim-chart database connection detail',
    );
  }
  if (state === 'failed-once' && options.retry) {
    await waitForDemoClaimChartDelay(500);
  }
  if (state === 'unauthorized') {
    throw new DemoClaimChartFixtureError(
      403,
      'private claim-chart authorization policy detail',
    );
  }
  const chart = demoCharts.find((item) => item.id === id);
  if (!chart) throw new Error('Claim chart not found.');
  const isSeedChart = id === demoChart.id;
  const chartFixture = state === 'long'
    ? {
        ...chart,
        name: `Claim chart — ${longContentFixture.identifier}`,
        patent_number: longContentFixture.filename,
        description: longContentFixture.url,
      }
    : chart;
  const columns = !isSeedChart || state === 'empty'
    ? []
    : state === 'long'
      ? demoChartColumns.map((column, index) => ({
          ...column,
          name: index === 0
            ? `Reference-${longContentFixture.identifier}`
            : column.name,
          description: longContentFixture.url,
        }))
      : demoChartColumns;
  const rows = !isSeedChart || state === 'empty'
    ? []
    : state === 'long'
      ? demoChartRows.map((row, index) => ({
          ...row,
          limitation_text: index === 0
            ? `${row.limitation_text} ${longContentFixture.identifier} ${longContentFixture.url}`
            : row.limitation_text,
        }))
      : demoChartRows;
  const cells = !isSeedChart || state === 'empty'
    ? []
    : state === 'long'
      ? demoChartCells.map((cell, index) => ({
          ...cell,
          mapping_text: index === 0
            ? `${cell.mapping_text ?? ''} ${longContentFixture.identifier} ${longContentFixture.url}`
            : cell.mapping_text,
        }))
      : state === 'generation-partial' || state === 'generation-slow'
        ? demoChartCells.map((cell) => ({
            ...cell,
            status: 'pending' as const,
          }))
        : demoChartCells;
  return {
    chart: structuredClone(chartFixture),
    columns: structuredClone(columns),
    rows: structuredClone(rows),
    cells: structuredClone(cells),
  };
}
export function createDemoChart(input: CreateChartInput): ClaimChart {
  if (getDemoClaimChartState() === 'create-failure' && demoChartCreateAttempts++ === 0) {
    throw new Error('Claim chart creation was not saved. Review the details and try again.');
  }
  if (
    input.matter_id
    && !demoMatters.some(
      (matter) => matter.id === input.matter_id && matter.status === 'active',
    )
  ) {
    throw new Error('The selected matter is no longer available.');
  }
  demoChartSequence += 1;
  const timestamp = `2026-07-23T12:${String(demoChartSequence).padStart(2, '0')}:00.000Z`;
  const chart: ClaimChart = {
    ...demoChart,
    id: `demo-chart-created-${demoChartSequence}`,
    user_id: DEMO_USER_ID,
    matter_id: input.matter_id ?? null,
    name: input.name.trim(),
    patent_number: input.patent_number?.trim() || null,
    description: input.description?.trim() || null,
    chart_type: input.chart_type ?? null,
    status: 'active',
    created_at: timestamp,
    updated_at: timestamp,
    evaluations: null,
  };
  demoCharts.unshift(chart);
  return structuredClone(chart);
}
export function createDemoChartColumn(input: CreateChartColumnInput): ClaimChartColumn {
  if (
    getDemoClaimChartDetailState() === 'mutation-failure'
    && demoChartColumnAttempts++ === 0
  ) {
    throw new DemoClaimChartFixtureError(
      503,
      'private column insert detail',
    );
  }
  return { id: `demo-chart-column-${crypto.randomUUID()}`, chart_id: input.chart_id, column_type: input.column_type, name: input.name.trim(), description: input.description ?? null, reference_citation: input.reference_citation ?? null, reference_number: input.reference_number ?? null, ordering: demoChartColumns.length, created_at: new Date().toISOString() };
}

/**
 * Workflow-launch wrappers preserve the ordinary claim-chart fixture while
 * adding route-local latency and recovery failures for W3.4.
 */
export async function createDemoWorkflowLaunchChart(
  input: CreateChartInput,
): Promise<ClaimChart> {
  const state = getDemoWorkflowState();
  if (state === 'launch-slow') {
    await waitForDemoWorkflowDelay(workflowFixture.launchDelayMs);
  }
  const attempt = demoWorkflowLaunchChartAttempts++;
  if (state === 'launch-failure' && attempt === 0) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow chart creation detail',
    );
  }
  if (state === 'launch-unauthorized') {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
    throw new DemoWorkflowFixtureError(
      403,
      'private workflow launch authorization detail',
    );
  }
  if (state === 'launch-missing') {
    throw new DemoWorkflowFixtureError(
      409,
      'private missing workflow dependency detail',
    );
  }
  return createDemoChart(input);
}

export async function createDemoWorkflowLaunchColumn(
  input: CreateChartColumnInput,
): Promise<ClaimChartColumn> {
  const state = getDemoWorkflowState();
  if (state === 'launch-slow') {
    await waitForDemoWorkflowDelay(workflowFixture.launchDelayMs);
  }
  const attempt = demoWorkflowLaunchColumnAttempts++;
  if (state === 'launch-partial' && attempt === 1) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow column seed detail',
    );
  }
  return createDemoChartColumn(input);
}

export async function archiveDemoWorkflowLaunchChart(
  chartId: string,
): Promise<void> {
  const state = getDemoWorkflowState();
  if (state === 'launch-slow') {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
  }
  const attempt = demoWorkflowLaunchCleanupAttempts++;
  if (state === 'launch-partial' && attempt === 0) {
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow cleanup detail',
    );
  }
  const index = demoCharts.findIndex((chart) => chart.id === chartId);
  if (index >= 0) demoCharts.splice(index, 1);
}
export function createDemoChartRow(input: CreateChartRowInput): ClaimChartRow {
  if (
    getDemoClaimChartDetailState() === 'mutation-failure'
    && demoChartRowAttempts++ === 0
  ) {
    throw new DemoClaimChartFixtureError(
      503,
      'private limitation insert detail',
    );
  }
  return { id: `demo-chart-row-${crypto.randomUUID()}`, chart_id: input.chart_id, claim_number: input.claim_number, limitation_index: input.limitation_index ?? 0, limitation_text: input.limitation_text.trim(), construction_notes: input.construction_notes ?? null, ordering: demoChartRows.length, created_at: new Date().toISOString() };
}
export async function createDemoChartCellResponse(
  chartId: string,
  rowId: string,
  columnId: string,
  signal?: AbortSignal,
): Promise<Response> {
  const state = getDemoClaimChartDetailState();
  const key = `${rowId}:${columnId}`;
  const attempt = demoChartCellAttempts.get(key) ?? 0;
  demoChartCellAttempts.set(key, attempt + 1);
  if (state === 'generation-slow') {
    await waitForDemoClaimChartDelay(2_000, signal);
  }
  if (
    state === 'generation-partial'
    && columnId === demoChartColumns[0]?.id
    && attempt === 0
  ) {
    return createCellResponse({
      type: 'error',
      message: 'private provider generation detail',
    }, 'error');
  }
  return createCellResponse({ chart_id: chartId, row_id: rowId, column_id: columnId, mapping_text: 'The reference partially describes the limitation, but the claimed feedback relationship is not express.', strength: 'moderate', notes: 'Review the cited paragraph in context.', citations: [{ label: 'Demo reference ¶ 42' }] });
}

function waitForDemoClaimChartDelay(
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      },
      { once: true },
    );
  });
}

// ---------------------------------------------------------------------------
// Tabular reviews
// ---------------------------------------------------------------------------

const demoReview: TabularReview = {
  id: 'demo-review-1', user_id: DEMO_USER_ID, matter_id: 'matter-aurora', name: 'Office Action reference comparison',
  description: 'Side-by-side review of the cited references.', practice: 'Prosecution', status: 'active', created_at: '2026-07-11T15:00:00.000Z', updated_at: NOW,
};
const demoReviewColumns: TabularColumn[] = [
  { id: 'review-col-summary', review_id: demoReview.id, title: 'Technical teaching', prompt: 'Summarize the core technical teaching.', format: 'text', ordering: 0, created_at: NOW },
  { id: 'review-col-gap', review_id: demoReview.id, title: 'Claim 1 gap', prompt: 'Identify the most important missing claim limitation.', format: 'text', ordering: 1, created_at: NOW },
  { id: 'review-col-risk', review_id: demoReview.id, title: 'Response risk', prompt: 'Rate the prosecution risk.', format: 'tag', ordering: 2, created_at: NOW },
];
const demoReviewRows: TabularRow[] = [
  { id: 'review-row-chen', review_id: demoReview.id, document_id: 'doc-chen', label: 'Chen reference', ordering: 0, created_at: NOW },
  { id: 'review-row-alvarez', review_id: demoReview.id, document_id: 'doc-alvarez', label: 'Alvarez reference', ordering: 1, created_at: NOW },
  { id: 'review-row-oa', review_id: demoReview.id, document_id: 'doc-office-action', label: 'Non-final Office Action', ordering: 2, created_at: NOW },
];
const demoReviewCells: TabularCell[] = demoReviewRows.flatMap((row, rowIndex) =>
  demoReviewColumns.map((column, columnIndex) => ({
    review_id: demoReview.id, row_id: row.id, column_id: column.id,
    content_summary: columnIndex === 0 ? ['Fixed-cycle sensor calibration.', 'Confidence scoring for noisy inputs.', 'Combines Chen and Alvarez under § 103.'][rowIndex]! : columnIndex === 1 ? ['No confidence-driven threshold adjustment.', 'No operating-threshold control.', 'Rationale does not explain the claimed feedback loop.'][rowIndex]! : ['Medium', 'Low', 'Medium'][rowIndex]!,
    citations: [{ label: row.label ?? 'Source' }], status: 'ready', error_message: null, last_regenerated_at: NOW, created_at: NOW, updated_at: NOW,
  })),
);
const demoReviews: TabularReview[] = [structuredClone(demoReview)];
let demoReviewListAttempts = 0;
let demoReviewCreateAttempts = 0;
let demoReviewSequence = 0;
let demoReviewColumnSequence = 0;
let demoReviewRowSequence = 0;
let demoReviewColumnAttempts = 0;
let demoReviewRowAttempts = 0;
let demoReviewRemoveRowAttempts = 0;
const demoReviewCellAttempts = new Map<string, number>();

export class DemoTabularReviewFixtureError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'DemoTabularReviewFixtureError';
  }
}

export function resetDemoTabularReviews(): void {
  demoReviews.splice(0, demoReviews.length, structuredClone(demoReview));
  demoReviewListAttempts = 0;
  demoReviewCreateAttempts = 0;
  demoReviewSequence = 0;
  demoReviewColumnSequence = 0;
  demoReviewRowSequence = 0;
  demoReviewColumnAttempts = 0;
  demoReviewRowAttempts = 0;
  demoReviewRemoveRowAttempts = 0;
  demoReviewCellAttempts.clear();
}

export async function listDemoReviews(matterId?: string): Promise<TabularReview[]> {
  const state = getDemoTabularReviewState();
  if (state === 'slow') {
    await new Promise((resolve) => setTimeout(resolve, 1_200));
  }
  if (state === 'load-failure' && demoReviewListAttempts++ === 0) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    throw new Error('Tabular reviews could not be loaded. Check your connection and try again.');
  }
  if (state === 'unauthorized') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    throw new DemoTabularReviewFixtureError(
      403,
      'private tabular-review list authorization policy detail',
    );
  }
  const available = state === 'empty'
    ? demoReviews.filter((review) => review.id.startsWith('demo-review-created-'))
    : state === 'long'
      ? demoReviews.map((review, index) =>
          index === 0
            ? {
                ...review,
                name: `Tabular review — ${longContentFixture.identifier}`,
                description: `${longContentFixture.url} ${longContentFixture.filename}`,
              }
            : review,
        )
      : demoReviews;
  const scoped = matterId
    ? available.filter((review) => review.matter_id === matterId)
    : available;
  if (matterId && state === 'scope-mismatch') {
    return structuredClone([
      ...scoped,
      {
        ...demoReview,
        id: 'demo-review-cross-matter',
        matter_id: 'matter-northstar',
        name: 'Cross-matter review must remain hidden',
      },
    ]);
  }
  return structuredClone(scoped);
}

export async function loadDemoReviewBundle(
  id: string,
  options: { retry?: boolean } = {},
): Promise<TabularReviewBundle> {
  const state = getDemoTabularReviewDetailState();
  if (state === 'slow') {
    await waitForDemoTabularDelay(1_200);
  }
  if (state === 'failed-once' && !options.retry) {
    await waitForDemoTabularDelay(300);
    throw new DemoTabularReviewFixtureError(
      503,
      'private tabular-review database connection detail',
    );
  }
  if (state === 'failed-once' && options.retry) {
    await waitForDemoTabularDelay(500);
  }
  if (state === 'unauthorized') {
    throw new DemoTabularReviewFixtureError(
      403,
      'private tabular-review authorization policy detail',
    );
  }
  const review = demoReviews.find((item) => item.id === id);
  if (!review) throw new Error('Tabular review not found.');
  const isSeedReview = id === demoReview.id;
  const reviewFixture = state === 'long'
    ? {
        ...review,
        name: `Tabular review — ${longContentFixture.identifier}`,
        description: `${longContentFixture.url} ${longContentFixture.filename}`,
      }
    : review;
  const columns = !isSeedReview || state === 'empty'
    ? []
    : state === 'long'
      ? demoReviewColumns.map((column, index) => ({
          ...column,
          title:
            index === 0
              ? `Teaching-${longContentFixture.identifier}`
              : column.title,
          prompt:
            index === 0
              ? `${column.prompt} ${longContentFixture.url}`
              : column.prompt,
        }))
      : demoReviewColumns;
  const rows = !isSeedReview || state === 'empty'
    ? []
    : state === 'long'
      ? demoReviewRows.map((row, index) => ({
          ...row,
          label:
            index === 0
              ? `Source-${longContentFixture.filename}`
              : row.label,
        }))
      : demoReviewRows;
  const cells = !isSeedReview || state === 'empty'
    ? []
    : state === 'long'
      ? demoReviewCells.map((cell, index) => ({
          ...cell,
          content_summary:
            index === 0
              ? `${cell.content_summary ?? ''} ${longContentFixture.identifier} ${longContentFixture.url}`
              : cell.content_summary,
          citations:
            index === 0
              ? [{ label: longContentFixture.filename, url: longContentFixture.url }]
              : cell.citations,
        }))
      : state === 'generation-partial' || state === 'generation-slow'
        ? demoReviewCells.map((cell) => ({
            ...cell,
            status: 'pending' as const,
          }))
        : demoReviewCells;
  return {
    review: structuredClone(reviewFixture),
    columns: structuredClone(columns),
    rows: structuredClone(rows),
    cells: structuredClone(cells),
  };
}

export function createDemoReview(input: CreateReviewInput): TabularReview {
  if (getDemoTabularReviewState() === 'create-failure' && demoReviewCreateAttempts++ === 0) {
    throw new Error('Tabular review creation was not saved. Review the details and try again.');
  }
  if (
    input.matter_id
    && !demoMatters.some(
      (matter) => matter.id === input.matter_id && matter.status === 'active',
    )
  ) {
    throw new Error('The selected matter is no longer available.');
  }
  demoReviewSequence += 1;
  const timestamp = `2026-07-23T13:${String(demoReviewSequence).padStart(2, '0')}:00.000Z`;
  const review: TabularReview = {
    ...demoReview,
    id: `demo-review-created-${demoReviewSequence}`,
    user_id: DEMO_USER_ID,
    matter_id: input.matter_id ?? null,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    practice: input.practice?.trim() || null,
    status: 'active',
    created_at: timestamp,
    updated_at: timestamp,
  };
  demoReviews.unshift(review);
  return structuredClone(review);
}
export function createDemoReviewColumn(
  input: CreateReviewColumnInput,
): TabularColumn {
  if (
    getDemoTabularReviewDetailState() === 'mutation-failure'
    && demoReviewColumnAttempts++ === 0
  ) {
    throw new DemoTabularReviewFixtureError(
      503,
      'private tabular column insert detail',
    );
  }
  demoReviewColumnSequence += 1;
  return {
    id: `demo-review-column-${demoReviewColumnSequence}`,
    review_id: input.review_id,
    title: input.title.trim(),
    prompt: input.prompt.trim(),
    format: input.format ?? 'text',
    ordering: demoReviewColumns.length + demoReviewColumnSequence,
    created_at: `2026-07-23T15:${String(demoReviewColumnSequence).padStart(2, '0')}:00.000Z`,
  };
}

export function createDemoReviewRow(input: CreateReviewRowInput): TabularRow {
  if (
    getDemoTabularReviewDetailState() === 'mutation-failure'
    && demoReviewRowAttempts++ === 0
  ) {
    throw new DemoTabularReviewFixtureError(
      503,
      'private tabular row insert detail',
    );
  }
  demoReviewRowSequence += 1;
  return {
    id: `demo-review-row-${demoReviewRowSequence}`,
    review_id: input.review_id,
    document_id: input.document_id,
    label: input.label ?? null,
    ordering: demoReviewRows.length + demoReviewRowSequence,
    created_at: `2026-07-23T16:${String(demoReviewRowSequence).padStart(2, '0')}:00.000Z`,
  };
}

export function deleteDemoReviewRow(): void {
  if (
    getDemoTabularReviewDetailState() === 'mutation-failure'
    && demoReviewRemoveRowAttempts++ === 0
  ) {
    throw new DemoTabularReviewFixtureError(
      503,
      'private tabular row delete detail',
    );
  }
}

export async function createDemoReviewCellResponse(
  reviewId: string,
  rowId: string,
  columnId: string,
  signal?: AbortSignal,
): Promise<Response> {
  const state = getDemoTabularReviewDetailState();
  const key = `${rowId}:${columnId}`;
  const attempt = demoReviewCellAttempts.get(key) ?? 0;
  demoReviewCellAttempts.set(key, attempt + 1);
  if (state === 'generation-slow') {
    await waitForDemoTabularDelay(2_000, signal);
  }
  if (
    state === 'generation-partial'
    && columnId === demoReviewColumns[0]?.id
    && attempt === 0
  ) {
    return createCellResponse(
      {
        type: 'error',
        message: 'private tabular generation provider detail',
      },
      'error',
    );
  }
  return createCellResponse({
    review_id: reviewId,
    row_id: rowId,
    column_id: columnId,
    content_summary:
      'The source partially addresses this criterion; the live record still shows a meaningful gap.',
    citations: [{ label: 'Demo source passage' }],
  });
}

function waitForDemoTabularDelay(
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(
      new DOMException('The operation was aborted.', 'AbortError'),
    );
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('The operation was aborted.', 'AbortError'));
      },
      { once: true },
    );
  });
}

function createCellResponse(
  payload: Record<string, unknown>,
  terminalEvent = 'cell_ready',
): Response {
  const body = `event: status\ndata: ${JSON.stringify({ step: 'analyze', message: 'Analyzing source' })}\n\nevent: ${terminalEvent}\ndata: ${JSON.stringify(payload)}\n\n`;
  return new Response(body, { status: 200, headers: { 'Content-Type': 'text/event-stream' } });
}

// ---------------------------------------------------------------------------
// Matter files and workflows
// ---------------------------------------------------------------------------

const demoFolders: MatterFolder[] = [
  { id: 'folder-office-actions', matter_id: 'matter-aurora', parent_folder_id: null, name: 'Office Actions', ordering: 0, created_at: NOW, updated_at: NOW },
  { id: 'folder-prior-art', matter_id: 'matter-aurora', parent_folder_id: null, name: 'Prior Art', ordering: 1, created_at: NOW, updated_at: NOW },
];
const demoDocuments: MatterDocumentWithFolder[] = [
  { id: 'doc-office-action', matter_id: 'matter-aurora', uploaded_by: DEMO_USER_ID, filename: '2026-05-18 Non-Final Office Action.pdf', file_type: 'application/pdf', storage_path: 'demo/office-action.pdf', size_bytes: 842_102, uploaded_at: NOW, folder_id: 'folder-office-actions' },
  { id: 'doc-response-draft', matter_id: 'matter-aurora', uploaded_by: DEMO_USER_ID, filename: 'Draft Response v3.docx', file_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', storage_path: 'demo/response.docx', size_bytes: 128_440, uploaded_at: NOW, folder_id: 'folder-office-actions' },
  { id: 'doc-chen', matter_id: 'matter-aurora', uploaded_by: DEMO_USER_ID, filename: 'Chen US20240123456.pdf', file_type: 'application/pdf', storage_path: 'demo/chen.pdf', size_bytes: 2_140_220, uploaded_at: NOW, folder_id: 'folder-prior-art' },
  { id: 'doc-alvarez', matter_id: 'matter-aurora', uploaded_by: DEMO_USER_ID, filename: 'Alvarez US20230098765.pdf', file_type: 'application/pdf', storage_path: 'demo/alvarez.pdf', size_bytes: 1_733_901, uploaded_at: NOW, folder_id: 'folder-prior-art' },
];
export function listDemoFolders(matterId: string): MatterFolder[] { return structuredClone(demoFolders.filter((item) => item.matter_id === matterId)); }
export function listDemoDocuments(matterId: string): MatterDocumentWithFolder[] { return structuredClone(demoDocuments.filter((item) => item.matter_id === matterId)); }
export function createDemoFolder(matterId: string, parentId: string | null, name: string): MatterFolder { const folder: MatterFolder = { id: `demo-folder-${crypto.randomUUID()}`, matter_id: matterId, parent_folder_id: parentId, name, ordering: demoFolders.length, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }; demoFolders.push(folder); return structuredClone(folder); }

export type DemoDocumentUploadContext = {
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
};

const demoDocumentUploadAttempts = new Map<string, number>();
let demoDocumentUploadSequence = 0;

function normalizedDemoFilename(filename: string): string {
  return filename.trim().toLocaleLowerCase('en-US');
}

function demoAbortError(): Error {
  const error = new Error('Upload cancelled.');
  error.name = 'AbortError';
  return error;
}

async function waitForDemoUploadCheckpoint(
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) throw demoAbortError();
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, milliseconds);
    const onAbort = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(demoAbortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Deterministic W2.2 document-upload behavior. The regular demo path stays
 * instant; `?uploadState=audit` adds an abortable progress timeline and
 * stable validation/failure inputs for semantic browser evidence.
 */
export async function uploadDemoDocument(
  matterId: string,
  file: File,
  context: DemoDocumentUploadContext = {},
): Promise<MatterDocument> {
  const audit = getDemoDocumentUploadState() === 'audit';
  const normalizedName = normalizedDemoFilename(file.name);

  if (audit) {
    if (!/\.(doc|docx|pdf)$/i.test(file.name)) {
      throw new Error(documentUploadFixture.unsupportedFailureMessage);
    }
    if (
      demoDocuments.some(
        (document) =>
          document.matter_id === matterId
          && normalizedDemoFilename(document.filename) === normalizedName,
      )
    ) {
      throw new Error(documentUploadFixture.duplicateFailureMessage);
    }
    for (const checkpoint of documentUploadFixture.progressCheckpoints) {
      await waitForDemoUploadCheckpoint(
        documentUploadFixture.checkpointDelayMs,
        context.signal,
      );
      context.onProgress?.(checkpoint);
    }
    if (
      normalizedName
      === normalizedDemoFilename(documentUploadFixture.retryOnceFilename)
    ) {
      const attemptKey = `${matterId}:${normalizedName}`;
      const attempts = demoDocumentUploadAttempts.get(attemptKey) ?? 0;
      demoDocumentUploadAttempts.set(attemptKey, attempts + 1);
      if (attempts === 0) {
        throw new Error(documentUploadFixture.retryFailureMessage);
      }
    }
  }

  if (context.signal?.aborted) throw demoAbortError();
  demoDocumentUploadSequence += 1;
  const documentId = audit
    ? `demo-document-upload-${demoDocumentUploadSequence}`
    : `demo-document-${crypto.randomUUID()}`;
  const doc: MatterDocumentWithFolder = {
    id: documentId,
    matter_id: matterId,
    uploaded_by: DEMO_USER_ID,
    filename: file.name,
    file_type: file.type || null,
    storage_path: `demo/${file.name}`,
    size_bytes: file.size,
    uploaded_at: audit ? NOW : new Date().toISOString(),
    folder_id: null,
  };
  demoDocuments.unshift(doc);
  return structuredClone(doc);
}

const demoWorkflowAssistant: WorkflowRow = {
  id: 'demo-workflow-1',
  user_id: DEMO_USER_ID,
  name: workflowFixture.assistantName,
  description: 'Prepare a tight issue list and proposed talking points.',
  practice: 'prosecution',
  prompt_md:
    'Prepare an examiner interview brief for {{application_number}} focused on {{issue}}.',
  columns_config: null,
  builtin_id: null,
  is_hidden: false,
  created_at: NOW,
  updated_at: NOW,
};
const demoWorkflowTabular: WorkflowRow = {
  id: 'demo-workflow-tabular',
  user_id: DEMO_USER_ID,
  name: workflowFixture.tabularName,
  description: 'Create a claim chart with three retained product evidence columns.',
  practice: 'litigation',
  prompt_md: '',
  columns_config: [
    { column_type: 'accused_product', name: 'Product Alpha' },
    { column_type: 'accused_product', name: 'Product Beta' },
    { column_type: 'reference', name: 'Technical specification' },
  ],
  builtin_id: null,
  is_hidden: false,
  created_at: NOW,
  updated_at: NOW,
};
const demoWorkflowSeeds = [demoWorkflowAssistant, demoWorkflowTabular] as const;
const demoWorkflows: WorkflowRow[] = [
  ...structuredClone(demoWorkflowSeeds),
];
let demoWorkflowListAttempts = 0;
let demoWorkflowCreateAttempts = 0;
let demoWorkflowUpdateAttempts = 0;
let demoWorkflowSequence = 0;

export class DemoWorkflowFixtureError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'DemoWorkflowFixtureError';
  }
}

export function resetDemoWorkflows(): void {
  demoWorkflows.splice(
    0,
    demoWorkflows.length,
    ...structuredClone(demoWorkflowSeeds),
  );
  demoWorkflowListAttempts = 0;
  demoWorkflowCreateAttempts = 0;
  demoWorkflowUpdateAttempts = 0;
  demoWorkflowSequence = 0;
  demoWorkflowLaunchChartAttempts = 0;
  demoWorkflowLaunchColumnAttempts = 0;
  demoWorkflowLaunchCleanupAttempts = 0;
}

export async function listDemoWorkflows(
  options: { retry?: boolean } = {},
): Promise<WorkflowRow[]> {
  const state = getDemoWorkflowState();
  const attempt = demoWorkflowListAttempts++;
  if (state === 'slow') {
    await waitForDemoWorkflowDelay(workflowFixture.listDelayMs);
  }
  if (state === 'failed-once' && !options.retry) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow list connection detail',
    );
  }
  if (state === 'failed-once' && options.retry) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
  }
  if (state === 'unauthorized') {
    throw new DemoWorkflowFixtureError(
      403,
      'private workflow authorization policy detail',
    );
  }
  if (state === 'stale' && attempt > 0 && !options.retry) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
    throw new DemoWorkflowFixtureError(
      503,
      'private stale workflow refresh detail',
    );
  }
  if (state === 'stale' && options.retry) {
    await waitForDemoWorkflowDelay(workflowFixture.retryDelayMs);
  }
  if (state === 'empty') {
    return BUILTIN_WORKFLOWS.map((workflow, index) => ({
      id: `demo-hidden-workflow-${index}`,
      user_id: DEMO_USER_ID,
      name: workflow.id,
      description: null,
      practice: workflow.practice,
      prompt_md: '',
      columns_config: null,
      builtin_id: workflow.id,
      is_hidden: true,
      created_at: NOW,
      updated_at: NOW,
    }));
  }
  if (state === 'launch-missing') {
    return [
      {
        ...demoWorkflowTabular,
        id: 'demo-workflow-missing-dependency',
        name: workflowFixture.missingDependencyName,
        columns_config: [
          { column_type: 'accused_product', name: '' },
        ],
      },
    ];
  }
  if (state === 'long') {
    return [
      {
        ...demoWorkflowAssistant,
        name: workflowFixture.longName,
        description: `${longContentFixture.url} ${longContentFixture.filename}`,
        prompt_md: `Review {{application_number}} and ${longContentFixture.identifier}`,
      },
      demoWorkflowTabular,
    ];
  }
  return structuredClone(demoWorkflows);
}

export async function createDemoWorkflow(
  input: CreateWorkflowInput,
): Promise<WorkflowRow> {
  const state = getDemoWorkflowState();
  const attempt = demoWorkflowCreateAttempts++;
  if (state.startsWith('mutation-')) {
    await waitForDemoWorkflowDelay(workflowFixture.mutationDelayMs);
  }
  if (state === 'mutation-failure' && attempt === 0) {
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow insert detail',
    );
  }
  if (state === 'mutation-unauthorized') {
    throw new DemoWorkflowFixtureError(
      403,
      'private workflow insert policy detail',
    );
  }
  if (state === 'mutation-stale') {
    throw new DemoWorkflowFixtureError(
      409,
      'private workflow collision detail',
    );
  }
  demoWorkflowSequence += 1;
  const timestamp = `2026-07-23T13:${String(demoWorkflowSequence).padStart(2, '0')}:00.000Z`;
  const row: WorkflowRow = {
    id: `demo-workflow-created-${demoWorkflowSequence}`,
    user_id: DEMO_USER_ID,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    practice: input.practice,
    prompt_md: input.prompt_md.trim(),
    columns_config: input.columns_config ?? null,
    builtin_id: null,
    is_hidden: false,
    created_at: timestamp,
    updated_at: timestamp,
  };
  demoWorkflows.unshift(row);
  return structuredClone(row);
}

export async function updateDemoWorkflow(
  id: string,
  patch: UpdateWorkflowInput,
): Promise<WorkflowRow> {
  const state = getDemoWorkflowState();
  const attempt = demoWorkflowUpdateAttempts++;
  if (state.startsWith('mutation-')) {
    await waitForDemoWorkflowDelay(workflowFixture.mutationDelayMs);
  }
  if (state === 'mutation-failure' && attempt === 0) {
    throw new DemoWorkflowFixtureError(
      503,
      'private workflow update detail',
    );
  }
  if (state === 'mutation-unauthorized') {
    throw new DemoWorkflowFixtureError(
      403,
      'private workflow update policy detail',
    );
  }
  if (state === 'mutation-stale') {
    throw new DemoWorkflowFixtureError(
      409,
      'private stale workflow update detail',
    );
  }
  const index = demoWorkflows.findIndex((item) => item.id === id);
  if (index < 0) {
    throw new DemoWorkflowFixtureError(404, 'private missing workflow row');
  }
  const current = demoWorkflows[index]!;
  const next = {
    ...current,
    ...patch,
    name: patch.name?.trim() ?? current.name,
    description:
      patch.description === undefined
        ? current.description
        : patch.description?.trim() || null,
    prompt_md: patch.prompt_md?.trim() ?? current.prompt_md,
    updated_at: '2026-07-23T14:00:00.000Z',
  };
  demoWorkflows[index] = next;
  return structuredClone(next);
}
export function deleteDemoWorkflow(id: string): void { const index = demoWorkflows.findIndex((item) => item.id === id); if (index >= 0) demoWorkflows.splice(index, 1); }

function waitForDemoWorkflowDelay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export const demoAccount = {
  email: demoUser.email!, display_name: 'Alex Morgan', tier: 'pro' as const, status: 'active', usage_today: 14, usage_limit: 100,
  current_period_end: '2026-08-01T00:00:00.000Z', stripe_customer_id: null, has_subscription: true, has_active_subscription: true, app_access: true,
};
